import { supabase } from './supabase';

/**
 * Simplified Admin Operations Module
 *
 * This module provides clean, direct database operations without workarounds.
 * RLS policies are properly configured in the database, so no need for error suppression.
 * All operations are transactional and secure.
 */

export const adminOperations = {
  /**
   * Insert data into a table
   */
  async insert<T = any>(table: string, data: any): Promise<{ data: T | null; error: any }> {
    const result = await supabase.from(table).insert(data).select();
    return { data: result.data as T, error: result.error };
  },

  /**
   * Update data in a table
   */
  async update<T = any>(
    table: string,
    data: any,
    filter: Record<string, any>
  ): Promise<{ data: T | null; error: any }> {
    let query = supabase.from(table).update(data);

    Object.entries(filter).forEach(([key, value]) => {
      query = query.eq(key, value);
    });

    const result = await query.select();
    return { data: result.data as T, error: result.error };
  },

  /**
   * Delete data from a table
   */
  async delete<T = any>(table: string, filter: Record<string, any>): Promise<{ data: T | null; error: any }> {
    let query = supabase.from(table).delete();

    Object.entries(filter).forEach(([key, value]) => {
      query = query.eq(key, value);
    });

    const result = await query.select();
    return { data: result.data as T, error: result.error };
  },

  /**
   * Select data from a table
   */
  async select<T = any>(
    table: string,
    columns?: string,
    filter?: Record<string, any>
  ): Promise<{ data: T | null; error: any }> {
    let query = supabase.from(table).select(columns || '*');

    if (filter) {
      Object.entries(filter).forEach(([key, value]) => {
        query = query.eq(key, value);
      });
    }

    const result = await query;
    return { data: result.data as T, error: result.error };
  },

  /**
   * Update provider specialties atomically using database function
   * This ensures all-or-nothing behavior with proper rollback on errors
   */
  async updateProviderSpecialties(
    doctorId: string,
    specialtyIds: string[],
    primarySpecialtyId?: string
  ): Promise<{ success: boolean; error?: string }> {
    const { data, error } = await supabase.rpc('update_provider_specialties', {
      p_doctor_id: doctorId,
      p_specialty_ids: specialtyIds,
      p_primary_specialty_id: primarySpecialtyId || null,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    if (data && !data.success) {
      return { success: false, error: data.error };
    }

    return { success: true };
  },

  /**
   * Update provider target groups atomically using database function
   * This ensures all-or-nothing behavior with proper rollback on errors
   */
  async updateProviderTargetGroups(
    doctorId: string,
    targetGroupIds: string[]
  ): Promise<{ success: boolean; error?: string }> {
    const { data, error } = await supabase.rpc('update_provider_target_groups', {
      p_doctor_id: doctorId,
      p_target_group_ids: targetGroupIds,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    if (data && !data.success) {
      return { success: false, error: data.error };
    }

    return { success: true };
  },

  /**
   * Update provider topics atomically using database function
   * This ensures all-or-nothing behavior with proper rollback on errors
   */
  async updateProviderTopics(
    doctorId: string,
    topicIds: string[]
  ): Promise<{ success: boolean; error?: string }> {
    const { data, error } = await supabase.rpc('update_provider_topics', {
      p_doctor_id: doctorId,
      p_topic_ids: topicIds,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    if (data && !data.success) {
      return { success: false, error: data.error };
    }

    return { success: true };
  },
};
